import React from 'react';

export const BroadcastPlayerRow = React.memo(({
  playerPos,
  onEventCreated
}) => {
  const { player, eligibility, is_captain, jersey_number } = playerPos;
  const isInactive = !eligibility.eligible || eligibility.isExpelled || eligibility.isSubstituted;
  const hasYellowCard = eligibility.yellowCards > 0;

  const fullName = `${player.first_name || ''} ${player.last_name || ''}`.trim() || 'Unknown';

  return (
    <div
      className={`
        flex items-center justify-between gap-2 p-2 rounded
        hover:bg-white/5
        transition-colors
        ${isInactive ? 'opacity-30' : ''}
      `}
    >
      {/* Left side: Avatar, Jersey, Captain, Name, Status */}
      <div className="flex items-center gap-2 flex-1 min-w-0">
        {/* Player Avatar */}
        <img
          src={player.photo_url || '/images/avatars/default-player.png'}
          alt={`${player.first_name || ''} ${player.last_name || ''}`}
          className="w-8 h-8 rounded-full object-cover flex-shrink-0"
          onError={(e) => { e.target.style.display = 'none'; }}
        />

        {/* Jersey Number - fixed width */}
        <div className="w-8 flex-shrink-0 text-center font-mono text-sm text-gray-400">
          #{jersey_number}
        </div>

        {/* Captain Badge - fixed width */}
        <div className="w-5 flex-shrink-0">
          {is_captain && (
            <div className="w-5 h-5 flex items-center justify-center bg-amber-600 text-white text-xs font-bold rounded">
              C
            </div>
          )}
        </div>

        {/* Player Name - flexible, can truncate */}
        <div className={`flex-1 text-sm font-medium truncate text-white ${isInactive ? 'line-through' : ''}`}>
          {fullName}
        </div>

        {/* Status Indicators */}
        {eligibility.isExpelled && (
          <div className="px-2 py-0.5 bg-red-900/50 text-red-400 text-xs font-bold rounded flex-shrink-0">
            EXPELLED
          </div>
        )}
        {hasYellowCard && !isInactive && (
          <div className="w-3 h-4 bg-yellow-400 rounded-sm flex-shrink-0"></div>
        )}
      </div>

      {/* Right side: Quick Action Buttons - Compact 48px, fixed width for perfect alignment */}
      {!isInactive && (
        <div className="flex gap-2 flex-shrink-0">
          {/* Goal Button */}
          <button
            onClick={() => onEventCreated('goal', playerPos)}
            className="relative w-12 h-12 rounded bg-gradient-to-b from-green-400/20 to-green-600/40 backdrop-blur-sm border-2 border-green-400/50 shadow-[0_0_20px_rgba(34,197,94,0.5),inset_0_2px_4px_rgba(255,255,255,0.2),inset_0_-2px_8px_rgba(34,197,94,0.4)] hover:shadow-[0_0_30px_rgba(34,197,94,0.7),inset_0_2px_4px_rgba(255,255,255,0.3),inset_0_-2px_12px_rgba(34,197,94,0.6)] transition-all active:translate-y-0.5"
            title="Goal"
          >
            <span className="text-white font-bold text-sm drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">G</span>
          </button>

          {/* Yellow Card Button */}
          <button
            onClick={() => onEventCreated('yellow_card', playerPos)}
            className="relative w-12 h-12 rounded bg-gradient-to-b from-yellow-400/20 to-yellow-600/40 backdrop-blur-sm border-2 border-yellow-400/50 shadow-[0_0_20px_rgba(234,179,8,0.5),inset_0_2px_4px_rgba(255,255,255,0.2),inset_0_-2px_8px_rgba(234,179,8,0.4)] hover:shadow-[0_0_30px_rgba(234,179,8,0.7),inset_0_2px_4px_rgba(255,255,255,0.3),inset_0_-2px_12px_rgba(234,179,8,0.6)] transition-all active:translate-y-0.5"
            title="Yellow Card"
          >
            <span className="text-white font-bold text-sm drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">Y</span>
          </button>

          {/* Red Card Button */}
          <button
            onClick={() => onEventCreated('red_card', playerPos)}
            className="relative w-12 h-12 rounded bg-gradient-to-b from-red-400/20 to-red-600/40 backdrop-blur-sm border-2 border-red-400/50 shadow-[0_0_20px_rgba(239,68,68,0.5),inset_0_2px_4px_rgba(255,255,255,0.2),inset_0_-2px_8px_rgba(239,68,68,0.4)] hover:shadow-[0_0_30px_rgba(239,68,68,0.7),inset_0_2px_4px_rgba(255,255,255,0.3),inset_0_-2px_12px_rgba(239,68,68,0.6)] transition-all active:translate-y-0.5"
            title="Red Card"
          >
            <span className="text-white font-bold text-sm drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">R</span>
          </button>

          {/* Substitution Button */}
          <button
            onClick={() => onEventCreated('substitution', playerPos)}
            className="relative w-12 h-12 rounded bg-gradient-to-b from-blue-400/20 to-blue-600/40 backdrop-blur-sm border-2 border-blue-400/50 shadow-[0_0_20px_rgba(59,130,246,0.5),inset_0_2px_4px_rgba(255,255,255,0.2),inset_0_-2px_8px_rgba(59,130,246,0.4)] hover:shadow-[0_0_30px_rgba(59,130,246,0.7),inset_0_2px_4px_rgba(255,255,255,0.3),inset_0_-2px_12px_rgba(59,130,246,0.6)] transition-all active:translate-y-0.5"
            title="Substitution"
          >
            <span className="text-white font-bold text-sm drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">S</span>
          </button>
        </div>
      )}
    </div>
  );
});

BroadcastPlayerRow.displayName = 'BroadcastPlayerRow';
